/**
 * How a slide looks.
 *
 * Separate from the app's own themes on purpose. `styles/themes/*.css` decides
 * what *you* look at while writing at 1am; this decides what a congregation
 * looks at on a screen twenty feet wide. They have almost nothing in common —
 * a preacher on the dark Ayu theme may still project onto white — so the two
 * settings are independent and neither reads the other.
 *
 * ## Geometry is not themeable
 *
 * Only colour and type face vary. Margins, sizes and spacing are fixed, because
 * they are what makes a deck legible from the back row, and they are the part
 * a well-meaning fiddle would ruin first. Every theme is therefore the same
 * slide in different clothes, which is also why the preview thumbnails can be
 * painted by the real renderer.
 */

/**
 * Slide width, in the pixels every renderer works in.
 *
 * Fixed across both aspect ratios: only the *height* of the frame changes, so
 * type sizes, margins and line lengths are identical whichever screen the deck
 * is going onto. A 4:3 slide is a 16:9 slide with more room underneath, not a
 * differently proportioned design that has to be tuned twice.
 */
export const SLIDE_WIDTH = 1920;

/** Points per inch of slide, so PPTX can convert these pixel sizes. */
export const PX_PER_INCH = SLIDE_WIDTH / 10;

/**
 * The shape of the screen being projected onto.
 *
 * Church AV is not standardised: a modern flat panel is 16:9, and a ceiling
 * projector bought fifteen years ago and still going is very often 4:3. Sending
 * a 16:9 deck to a 4:3 projector letterboxes it — the text ends up in a band
 * across the middle of the screen with black above and below, which is exactly
 * the legibility the fixed geometry above exists to protect.
 *
 * Both widths are 1920, so `PX_PER_INCH` is 192 in either case and the frame
 * lands on PowerPoint's own page sizes exactly: 10in × 5.625in for 16:9, and
 * 10in × 7.5in for 4:3. Anything else and the PDF comes out at a size the
 * printer then scales on its own terms.
 */
export interface SlideAspect {
  id: SlideAspectId;
  /** How it is offered in Settings. */
  name: string;
  /** What it is for, in one line. */
  description: string;
  width: number;
  height: number;
  /** The matching `pptxgenjs` layout name. */
  pptxLayout: "LAYOUT_16x9" | "LAYOUT_4x3";
  /** The value Marp's `size:` frontmatter key takes. */
  marpSize: string;
}

export type SlideAspectId = "16:9" | "4:3";

export const SLIDE_ASPECTS: SlideAspect[] = [
  {
    id: "16:9",
    name: "16:9 widescreen",
    description: "Flat panels, modern projectors, anything bought this decade.",
    width: SLIDE_WIDTH,
    height: 1080,
    pptxLayout: "LAYOUT_16x9",
    marpSize: "16:9",
  },
  {
    id: "4:3",
    name: "4:3 standard",
    description: "Older ceiling projectors and square screens. Taller, not wider.",
    width: SLIDE_WIDTH,
    height: 1440,
    pptxLayout: "LAYOUT_4x3",
    marpSize: "4:3",
  },
];

export const DEFAULT_SLIDE_ASPECT: SlideAspectId = "16:9";

/**
 * Turn a stored id into an aspect to render with.
 *
 * Falls back rather than throwing, for the same reason `resolveSlideTheme`
 * does: a value from a future version or a mangled localStorage entry must
 * never stop a deck from rendering.
 */
export function resolveSlideAspect(id: string | null | undefined): SlideAspect {
  return (
    SLIDE_ASPECTS.find((aspect) => aspect.id === id) ??
    (SLIDE_ASPECTS.find((aspect) => aspect.id === DEFAULT_SLIDE_ASPECT) as SlideAspect)
  );
}

/** The default frame, for callers that have not been handed one. */
export const DEFAULT_ASPECT = resolveSlideAspect(DEFAULT_SLIDE_ASPECT);

/**
 * Fixed measurements, shared by every theme and every renderer.
 *
 * The canvas, the PowerPoint text boxes and the print sheet all read these, so
 * a deck exported three ways is the same deck.
 */
export const SLIDE_GEOMETRY = {
  margin: 130,
  titleSize: 88,
  titleOnlySize: 116,
  bodySize: 54,
  quoteSize: 66,
  lineGap: 1.35,
  /** Strip reserved at the foot for the rule and slide number. */
  footer: 90,
} as const;

/**
 * The two faces a slide can use.
 *
 * Named rather than free text: a font that is not installed on the machine
 * doing the projecting falls back to something unpredictable, and a slide is
 * the worst possible place to discover that. These two stacks resolve on every
 * platform that will ever open one of these files.
 */
export const SLIDE_FACES = {
  sans: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
} as const;

/** The face names PowerPoint needs, which take a family rather than a stack. */
export const PPTX_FACES = { sans: "Helvetica", serif: "Georgia" } as const;

export type SlideFace = keyof typeof SLIDE_FACES;

export interface SlideTheme {
  id: string;
  name: string;
  description: string;
  /** The page. */
  background: string;
  /** Titles and body copy. */
  ink: string;
  /** Slide numbers, image placeholders — anything that must recede. */
  soft: string;
  /** The footer rule. Ignored entirely when `ornament` is false. */
  accent: string;
  titleFace: SlideFace;
  quoteFace: SlideFace;
  /**
   * Draw the accent rule and the slide number at all.
   *
   * False means *nothing* on the page but the words. Not a faint rule, not a
   * discreet number — nothing. See Void.
   */
  ornament: boolean;
}

export type SlideThemeId = "void" | "paper" | "slate" | "chapel" | "custom";

export const SLIDE_THEMES: SlideTheme[] = [
  {
    id: "chapel",
    name: "Chapel",
    description: "White page, deep indigo ink. The safe one for a bright room.",
    background: "#FFFFFF",
    ink: "#1B1D24",
    soft: "#5A5D68",
    accent: "#3B3E8C",
    titleFace: "sans",
    quoteFace: "serif",
    ornament: true,
  },
  {
    id: "void",
    name: "Void",
    description: "Black page, white text, nothing else at all.",
    background: "#000000",
    ink: "#FFFFFF",
    soft: "#8A8A8A",
    // Never drawn — kept only so a switch to another theme has somewhere sane
    // to start from.
    accent: "#FFFFFF",
    titleFace: "sans",
    quoteFace: "sans",
    ornament: false,
  },
  {
    id: "paper",
    name: "Paper",
    description: "Warm printed page, serif throughout. Reads like an order of service.",
    background: "#F7F4EC",
    ink: "#22201B",
    soft: "#6E695C",
    accent: "#8A6A3B",
    titleFace: "serif",
    quoteFace: "serif",
    ornament: true,
  },
  {
    id: "slate",
    name: "Slate",
    description: "Charcoal ground, warm off-white. Dark without the glare of pure black.",
    background: "#16181D",
    ink: "#ECEAE4",
    soft: "#8B8F98",
    accent: "#C9A227",
    titleFace: "sans",
    quoteFace: "serif",
    ornament: true,
  },
];

export const DEFAULT_SLIDE_THEME: SlideThemeId = "chapel";

/**
 * The starting point for Custom.
 *
 * Chapel rather than an empty palette: someone opening the custom editor should
 * find a working slide to adjust, not a blank they have to make legible first.
 */
export const CUSTOM_SLIDE_THEME_BASE: SlideTheme = {
  ...(SLIDE_THEMES.find((theme) => theme.id === "chapel") as SlideTheme),
  id: "custom",
  name: "Custom",
  description: "Your own colours.",
};

/** What gets stored for a custom theme — the parts a person can change. */
export type CustomSlideTheme = Pick<
  SlideTheme,
  "background" | "ink" | "soft" | "accent" | "titleFace" | "quoteFace" | "ornament"
>;

export function customDefaults(): CustomSlideTheme {
  const { background, ink, soft, accent, titleFace, quoteFace, ornament } =
    CUSTOM_SLIDE_THEME_BASE;
  return { background, ink, soft, accent, titleFace, quoteFace, ornament };
}

/**
 * Turn a stored id (plus the custom palette) into a theme to render with.
 *
 * Falls back to the default rather than throwing: an id from a future version,
 * or a corrupted localStorage entry, must never stop a deck from rendering.
 */
export function resolveSlideTheme(
  id: string | null | undefined,
  custom?: Partial<CustomSlideTheme> | null,
): SlideTheme {
  if (id === "custom") {
    return { ...CUSTOM_SLIDE_THEME_BASE, ...(custom ?? {}) };
  }
  return (
    SLIDE_THEMES.find((theme) => theme.id === id) ??
    (SLIDE_THEMES.find((theme) => theme.id === DEFAULT_SLIDE_THEME) as SlideTheme)
  );
}

/** Is this a colour we can hand to canvas, CSS and PPTX alike? */
export function isHexColour(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value.trim());
}

/**
 * Everything a renderer needs beyond the slide itself.
 *
 * Grouped rather than passed as two more positional arguments: every path that
 * paints a slide — canvas, PNG, PPTX, print — needs exactly this pair, and a
 * caller that forgets one would silently export a deck in the wrong shape.
 */
export interface SlideStyle {
  theme: SlideTheme;
  aspect: SlideAspect;
}

/** The pair to fall back on when a caller has not been given one. */
export const DEFAULT_SLIDE_STYLE: SlideStyle = {
  theme: resolveSlideTheme(DEFAULT_SLIDE_THEME),
  aspect: DEFAULT_ASPECT,
};

/** Relative luminance of a `#rrggbb` colour, per WCAG. */
function luminance(colour: string): number {
  const hex = colour.trim().replace("#", "");
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/**
 * WCAG contrast ratio between two hex colours, 1 to 21.
 *
 * Used to warn — never to refuse. A custom palette belongs to whoever set it,
 * and the room they are projecting into is one this app cannot see. But ink at
 * 2:1 against its page is invisible from the back row, and finding that out
 * mid-service is the failure this exists to prevent.
 */
export function contrastRatio(foreground: string, background: string): number {
  if (!isHexColour(foreground) || !isHexColour(background)) return 21;
  const light = Math.max(luminance(foreground), luminance(background));
  const dark = Math.min(luminance(foreground), luminance(background));
  return (light + 0.05) / (dark + 0.05);
}

/**
 * The floor for text on a projected slide.
 *
 * Well above WCAG's 4.5:1 for body copy, because a projector in a lit room
 * washes out far more contrast than a screen at arm's length does.
 */
export const MIN_SLIDE_CONTRAST = 7;
